<template>
    <div class="cancel-order-popover-content">
        <div class="whole-order-text">
            {{ trans.cancelOrderPopover.wholeOrderText }}
        </div>

        <VButton @click="cancelWholeOrder" :disabled="cancelButtonDisabled" class="cancel-order-btn primary rounded">
            {{ cancelButtonText }}
        </VButton>
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { CancellationReasonPopover } from "@/app/popover/CancellationReasonPopover";
    import type { IAddCancellationCmd } from "@/data/commands/order/AddCancellationCmd";
    import { Factory } from "@/data/dao/Factory";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import { CancellationMode, RefundMode } from "@/data/model/Enums";
    import { computed, onMounted, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);

    const cancelButtonText = computed(() => {
        return `${trans.cancelOrderPopover.yesCancel} - CHF ${cancellableAmount.value.toFixed(2)}`;
    });

    const cancelButtonDisabled = ref(false);
    const cancellableAmount = ref<number>(0);

    const props = defineProps<{
        orderId: string;
        locationId: string;
        onOrderCancellationSucceeded?: () => void;
        onOrderCancellationFailed?: (errorMessage: string) => void;
    }>();

    onMounted(async () => {
        const order = await orderDao.getSingle(props.orderId, { showLocationOrderItemsIfLocationIsNotOwner: true });
        cancellableAmount.value = order.totalAmount - order.totalCancellationAmount;
    });

    async function cancelWholeOrder(): Promise<void> {
        const data: IAddCancellationCmd = {
            refundMode: RefundMode.Online,
            mode: CancellationMode.WholeOrder,
            locationId: props.locationId,
            isRefundApprovedByOperator: false,
            positions: undefined,
        };

        const popover = new CancellationReasonPopover(
            props.locationId,
            props.orderId,
            cancellableAmount.value,
            data,
            () => {},
            (errorCode, errorMessage) => {
                if (props.onOrderCancellationFailed != null) props.onOrderCancellationFailed(errorMessage);
                popover.hide();
            },
            () => {
                if (props.onOrderCancellationSucceeded != null) props.onOrderCancellationSucceeded();
                popover.hide();
            }
        );
        popover.show();
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .cancel-order-popover-content {
        padding-bottom: 14px;

        .offline-mode-section {
            display: flex;
            padding: 1em 0;

            > .checkbox {
                width: 20px;
                height: 20px;
                box-sizing: border-box;
                border: 1px solid #c9c9c9;
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: 20px;
                cursor: pointer;
                font-size: 0;
                text-align: center;
                transition: border-color 0.3s variables.$easeOutExpo;
            }

            > .name {
                text-align: left;
                font-size: 14px;
                padding-left: 14px;
                color: colors.$fontColor;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;
                cursor: pointer;
            }

            &.selected {
                > .checkbox {
                    border-color: colors.$green;

                    &:before {
                        font-family: "Font Awesome 5 Pro";
                        font-weight: 400;
                        content: "\f00c";
                        font-size: 14px;
                        color: colors.$green;
                        display: inline-block;
                        vertical-align: top;
                        line-height: 20px;
                    }
                }
            }
        }

        > .whole-order-text {
            text-align: center;
            padding: 1em 0;
            font-size: 12px;
        }

        .cancel-order-btn.v-btn.primary {
            font-size: 17px;
            margin-top: 0.625em;
            height: 2.75em;
            line-height: 2.75em;
            padding: 0 2em;
            border-radius: 1.5em !important;
            width: 100%;

            &.hidden {
                display: none;
            }
        }
    }
</style>
